import React, { useEffect, useRef, useState } from 'react';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import { useAuth } from 'services/auth-service';
import SelectInput from 'components/elements/Form/select/selectInput';
import { useQuery } from 'react-query';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import { OrganizationProps } from 'services/hooks/organization-service';
import { AccessIntegrationName, defaultUUIDValue } from 'utils/constants';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';

export default function RoleSelectionForm({
  Controller,
  methods,
  editUUID,
  accountData,
  buildingData,
  orgData
}: any) {
  const {
    state: { user }
  } = useAuth();

  const [accountOptions, setaccountOptions] = useState<any>([]);
  const [buildingOptions, setbuildingOptions] = useState<any>([]);
  const [orgoptions, setorgoptions] = useState<any>([]);
  const [getAccountByIdResult, setGetAccountByIdResult] = useState<any>(undefined);
  const [isIntegrationDisabled, setIsIntegrationDisabled] = useState(false);
  const [noneIntegrationUuid, setNoneIntegrationUuid] = useState<string>('');

  const accountWatch = methods.watch('accountUuid');
  const orgWatch = methods.watch('organizationUuid');
  const buildingWatch = methods.watch('buildingUuid');

  const orgWatchRef = useRef(orgWatch);

  useEffect(() => {
    orgWatchRef.current = orgWatch;
  }, [orgWatch]);

  const formatedOptions = (value: any, label: string) => {
    return { value: value, label: label };
  };

  // Fetch all integrations
  const { data: integrationsData } = useQuery<IntegrationProps[], Error>(
    'integrations',
    () => IntegrationService.findAllIntegrations(),
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integrations'));
      }
    }
  );

  useEffect(() => {
    if (integrationsData) {
      const noneIntegration = integrationsData.find(
        (integration) => integration.name === AccessIntegrationName.None
      );
      setNoneIntegrationUuid(noneIntegration ? noneIntegration.uuid || '' : '');
    }
  }, [integrationsData]);

  // Query the accountwatch using getquery (GET request) by account id
  const { data, refetch } = useQuery<AccountProps, Error>(
    ['account', user?.role === 'SUPER' || user?.role === 'ACCOUNT', accountWatch],
    () => AccountService.findAccountById(accountWatch),
    {
      enabled: !!accountWatch && (user?.role === 'SUPER' || user?.role === 'ACCOUNT'),
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  // Set the data in useState() variable getAccountByIdResult
  useEffect(() => {
    if (data && accountWatch) {
      refetch();
      setGetAccountByIdResult(data);
    } else {
      setGetAccountByIdResult(undefined);
    }
  }, [accountWatch, data, refetch]);

  //UseEffect when the role is super
  useEffect(() => {
    if (getAccountByIdResult && user?.role === 'SUPER') {
      //To populate building options
      setbuildingOptions(
        getAccountByIdResult?.buildings?.map((building: { uuid: string; name: string }) =>
          formatedOptions(building?.uuid, building?.name)
        )
      );

      // If Organization is selected pre populate the building associated with organization
      // and pre populate integration if selected organization has one
      if (orgWatch) {
        getAccountByIdResult?.buildings?.forEach(
          (element: { uuid: string; name: string; organizations: OrganizationProps[] }) => {
            const matchingOrg = element?.organizations.find(
              (org: { uuid: string }) => org?.uuid === orgWatch
            );

            if (matchingOrg) {
              methods.setValue('buildingUuid', element?.uuid);
              if (!editUUID) {
                if (
                  matchingOrg?.integrationUuid &&
                  matchingOrg?.integrationUuid !== defaultUUIDValue
                ) {
                  methods.setValue('integrationUuid', matchingOrg?.integrationUuid);
                  setIsIntegrationDisabled(true);
                } else {
                  methods.setValue('integrationUuid', noneIntegrationUuid);
                  setIsIntegrationDisabled(true);
                }
              }
            }
            return;
          }
        );
      } else {
        methods.setValue('integrationUuid', '');
        setIsIntegrationDisabled(false);
      }
    } else if (user?.role === 'SUPER' && !editUUID) {
      // Reset building & organization selection and building & organization options when account field is cleared and form is not in edit mode
      methods.setValue('buildingUuid', '');
      methods.setValue('organizationUuid', '');
      methods.setValue('integrationUuid', '');
      setIsIntegrationDisabled(false);
      setbuildingOptions([]);
      setorgoptions([]);
    }
  }, [getAccountByIdResult, orgWatch, methods, user?.role, editUUID, noneIntegrationUuid]);

  // Effect to load organization options belonging to the selected building (if any) when the role is Super
  useEffect(() => {
    if (getAccountByIdResult && user?.role === 'SUPER') {
      // To populate Organization options
      if (buildingWatch) {
        const populateSelectedOrg: { value: string; label: string }[] = [];
        const selectedBuilding = getAccountByIdResult?.buildings?.find(
          (building: { uuid: string }) => building.uuid === buildingWatch
        );

        if (selectedBuilding) {
          selectedBuilding?.organizations?.map((org: { uuid: string; name: string }) =>
            populateSelectedOrg.push(formatedOptions(org?.uuid, org?.name))
          );
          setorgoptions(populateSelectedOrg);

          const orgBelongsToBuilding = selectedBuilding.organizations.some(
            (org: { uuid: string }) => org.uuid === orgWatchRef.current
          );

          if (!orgBelongsToBuilding) {
            methods.setValue('organizationUuid', '');
          }
        } else {
          methods.setValue('buildingUuid', '');
        }
      } else {
        if (!editUUID) {
          methods.setValue('organizationUuid', '');
        }

        const populateorg: { value: string; label: string }[] = [];
        getAccountByIdResult?.buildings?.forEach(
          (building: { organizations: OrganizationProps[] }) =>
            building?.organizations?.map((org: { uuid: string; name: string }) =>
              populateorg.push(formatedOptions(org?.uuid, org?.name))
            )
        );
        setorgoptions(populateorg);
      }
    }
  }, [
    getAccountByIdResult,
    getAccountByIdResult?.buildings,
    buildingWatch,
    editUUID,
    methods,
    user?.role
  ]);

  // Useeffect when the role is Account
  useEffect(() => {
    if (accountData && user?.role === 'ACCOUNT') {
      methods.setValue('accountUuid', accountData?.uuid);
      setaccountOptions([{ value: accountData?.uuid, label: accountData?.name }]);

      //To populate building options
      setbuildingOptions(
        accountData?.buildings?.map((building: { uuid: string; name: string }) =>
          formatedOptions(building?.uuid, building?.name)
        )
      );

      // If Organization is selected pre populate the building associated with organization
      // and pre populate integration if selected organization has one
      if (orgWatch) {
        accountData?.buildings?.forEach(
          (element: { uuid: string; name: string; organizations: OrganizationProps[] }) => {
            const matchingOrg = element?.organizations.find(
              (org: { uuid: string }) => org?.uuid === orgWatch
            );

            if (matchingOrg) {
              methods.setValue('buildingUuid', element?.uuid);
              if (!editUUID) {
                if (
                  matchingOrg?.integrationUuid &&
                  matchingOrg?.integrationUuid !== defaultUUIDValue
                ) {
                  methods.setValue('integrationUuid', matchingOrg?.integrationUuid);
                  setIsIntegrationDisabled(true);
                } else {
                  methods.setValue('integrationUuid', noneIntegrationUuid);
                  setIsIntegrationDisabled(true);
                }
              }
            }
            return;
          }
        );
      } else {
        methods.setValue('integrationUuid', '');
        setIsIntegrationDisabled(false);
      }
    }
  }, [accountData, methods, orgWatch, user?.role, editUUID, noneIntegrationUuid]);

  // Effect to load organization options belonging to the selected building (if any) when the role is Account
  useEffect(() => {
    if (accountData && user?.role === 'ACCOUNT') {
      // To populate Organization options
      if (buildingWatch) {
        const populateSelectedOrg: { value: string; label: string }[] = [];
        const selectedBuilding = accountData?.buildings?.find(
          (building: { uuid: string }) => building.uuid === buildingWatch
        );

        if (selectedBuilding) {
          selectedBuilding?.organizations?.map((org: { uuid: string; name: string }) =>
            populateSelectedOrg.push(formatedOptions(org?.uuid, org?.name))
          );
          setorgoptions(populateSelectedOrg);

          const orgBelongsToBuilding = selectedBuilding.organizations.some(
            (org: { uuid: string }) => org.uuid === orgWatchRef.current
          );

          if (!orgBelongsToBuilding) {
            methods.setValue('organizationUuid', '');
          }
        }
      } else {
        if (!editUUID) {
          methods.setValue('organizationUuid', '');
        }

        const populateorg: { value: string; label: string }[] = [];
        accountData?.buildings?.forEach((building: { organizations: OrganizationProps[] }) =>
          building?.organizations?.map((org: { uuid: string; name: string }) =>
            populateorg.push(formatedOptions(org?.uuid, org?.name))
          )
        );
        setorgoptions(populateorg);
      }
    }
  }, [accountData, accountData?.buildings, buildingWatch, editUUID, methods, user?.role]);

  // Useeffect when the role is building
  useEffect(() => {
    if (buildingData && user?.role === 'BUILDING') {
      methods.setValue('buildingUuid', buildingData?.uuid);
      setbuildingOptions([{ value: buildingData?.uuid, label: buildingData?.name }]);

      setorgoptions(
        buildingData?.organizations?.map((org: { uuid: any; name: string }) =>
          formatedOptions(org?.uuid, org?.name)
        )
      );

      // If selected Organization has an integration pre-populate integration field
      if (orgWatch) {
        const selectedOrg = buildingData?.organizations.find(
          (org: { uuid: string; integrationUuid?: string }) => org?.uuid === orgWatch
        );
        if (selectedOrg && !editUUID) {
          if (selectedOrg?.integrationUuid && selectedOrg?.integrationUuid !== defaultUUIDValue) {
            methods.setValue('integrationUuid', selectedOrg?.integrationUuid);
            setIsIntegrationDisabled(true);
          } else {
            methods.setValue('integrationUuid', noneIntegrationUuid);
            setIsIntegrationDisabled(true);
          }
        } else {
          methods.setValue('integrationUuid', '');
          setIsIntegrationDisabled(false);
        }
      } else {
        methods.setValue('integrationUuid', '');
        setIsIntegrationDisabled(false);
      }
    }
  }, [buildingData, methods, user?.role, orgWatch, editUUID, noneIntegrationUuid]);

  //UseEffect when the role is organization
  useEffect(() => {
    if (orgData && user?.role === 'ORG') {
      methods.setValue('organizationUuid', orgData?.uuid);
      setorgoptions([{ value: orgData?.uuid, label: orgData?.name }]);
    }
  }, [orgData, methods, user?.role]);

  const renderRoleBasedDetails = () => {
    switch (user?.role) {
      case 'SUPER':
        return (
          <>
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Account"
              placeholder="Select Account"
              queryname="accounts"
              name="accountUuid"
              errors={methods.formState.errors}
              disabled={editUUID && true}
              query={AccountService.findAllAccounts}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Building"
              placeholder="Select Building"
              name="buildingUuid"
              errors={methods.formState.errors}
              disabled={editUUID && true}
              options={buildingOptions}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose an Organization"
              placeholder="Select Organization"
              errors={methods.formState.errors}
              name="organizationUuid"
              disabled={editUUID && true}
              options={orgoptions}
            />
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Integration"
              placeholder="Select Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
              disabled={isIntegrationDisabled || !!editUUID}
            />
          </>
        );
      case 'ACCOUNT':
        return (
          <>
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose an Account"
              name="accountUuid"
              errors={methods.formState.errors}
              disabled
              placeholder="Select Account"
              options={accountOptions}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Building"
              name="buildingUuid"
              errors={methods.formState.errors}
              disabled={editUUID && true}
              placeholder="Select Building"
              options={buildingOptions}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose an Organization"
              name="organizationUuid"
              errors={methods.formState.errors}
              disabled={editUUID && true}
              placeholder="Select Organization"
              options={orgoptions}
            />
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Integration"
              placeholder="Select an Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
              disabled={isIntegrationDisabled || !!editUUID}
            />
          </>
        );

      case 'BUILDING':
        return (
          <>
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Building"
              name="buildingUuid"
              errors={methods.formState.errors}
              disabled
              placeholder="Select Building"
              options={buildingOptions}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose an Organization"
              name="organizationUuid"
              errors={methods.formState.errors}
              disabled={editUUID && true}
              placeholder="Select Organization"
              options={orgoptions}
            />
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Integration"
              placeholder="Select an Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
              disabled={isIntegrationDisabled || !!editUUID}
            />
          </>
        );
      case 'ORG':
        return (
          <>
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose an Organization"
              name="organizationUuid"
              errors={methods.formState.errors}
              disabled
              placeholder="Select Organization"
              options={orgoptions}
            />
          </>
        );

      default:
        console.log('Role is missing');
        return null;
    }
  };

  return renderRoleBasedDetails();
}
